import { dateTimeFormat } from '@magicdoorinc/date';
import { currency } from '@magicdoorinc/ui';
import IconCharge from '~/assets/images/leases/transactions/newCharge.svg?component-solid';
import IconCredit from '~/assets/images/leases/transactions/newCredit.svg?component-solid';
import IconPayment from '~/assets/images/leases/transactions/newPayment.svg?component-solid';
import IconReturnedDollar from '~/assets/images/leases/transactions/returned_dollar.svg?component-solid';
import IconTransfer from '~/assets/images/leases/transactions/transfer.svg?component-solid';
import {
  LeaseChargeLedgerEntryType,
  LeaseChargeLedgerEntryStatus,
  LeaseDepositLedgerEntryType,
  LeaseDepositTransactionStatus,
} from '~/swagger/portal/Api';
import { Dict } from '~/utils/dict';
import type { Option } from '@magicdoorinc/ui';
import type { Component } from 'solid-js';
import type { LeaseChargeLedgerEntryDto } from '~/swagger/portal/Api';

type TransactionTypeMeta = {
  icon?: Component;
  bgColor: string;
  deleteFn?: (leaseId: string, id: string) => Promise<void>;
};

type DepositTypeMeta = {
  icon?: Component;
  bgColor: string;
  deleteFn?: (leaseId: string, id: string) => Promise<void>;
};

export const TRANSACTION_TYPE_DICT = new Dict<LeaseChargeLedgerEntryType, TransactionTypeMeta>([
  {
    label: 'Charge',
    value: LeaseChargeLedgerEntryType.Charge,
    meta: {
      icon: IconCharge,
      bgColor: '#FCE7FF',
    },
  },
  {
    label: 'Late Fee',
    value: LeaseChargeLedgerEntryType.LateFee,
    meta: {
      icon: IconCharge,
      bgColor: '#FFEDE1',
    },
  },
  {
    label: 'Credit',
    value: LeaseChargeLedgerEntryType.Credit,
    meta: {
      icon: IconCredit,
      bgColor: '#FFF9E8',
    },
  },
  {
    label: 'Payment',
    value: LeaseChargeLedgerEntryType.Payment,
    meta: {
      icon: IconPayment,
      bgColor: '#D4ECFF',
    },
  },
  {
    label: 'Return',
    value: LeaseChargeLedgerEntryType.Return,
    meta: {
      icon: IconReturnedDollar,
      bgColor: '#FFE7F1',
    },
  },
  {
    label: 'Transfer',
    value: LeaseChargeLedgerEntryType.Transfer,
    meta: {
      icon: IconTransfer,
      bgColor: '#FFE6DB',
    },
  },
  {
    label: 'Payout',
    value: LeaseChargeLedgerEntryType.Payout,
    meta: {
      icon: IconTransfer,
      bgColor: '#FFE6DB',
    },
  },
]);

export const DEPOSIT_TYPE_DICT = new Dict<LeaseDepositLedgerEntryType, DepositTypeMeta>([
  {
    label: 'Deposit',
    value: LeaseDepositLedgerEntryType.Deposit,
    meta: {
      icon: IconCharge,
      bgColor: '#FCE7FF',
    },
  },
  {
    label: 'Payment',
    value: LeaseDepositLedgerEntryType.Payment,
    meta: {
      icon: IconPayment,
      bgColor: '#D4ECFF',
    },
  },
  {
    label: 'Return',
    value: LeaseDepositLedgerEntryType.Return,
    meta: {
      icon: IconReturnedDollar,
      bgColor: '#FFE7F1',
    },
  },
  {
    label: 'Transfer',
    value: LeaseDepositLedgerEntryType.Transfer,
    meta: {
      icon: IconTransfer,
      bgColor: '#FFE6DB',
    },
  },
  {
    label: 'Released',
    value: LeaseDepositLedgerEntryType.Payout,
    meta: {
      icon: IconTransfer,
      bgColor: '#FFE6DB',
    },
  },
]);

export const CHARGE_STATUS_DICT = new Dict<LeaseChargeLedgerEntryStatus, { textClass: string }>([
  {
    label: 'Future',
    value: LeaseChargeLedgerEntryStatus.Future,
    meta: {
      textClass: 'bg-success-light text-success',
    },
  },
  {
    label: 'Due',
    value: LeaseChargeLedgerEntryStatus.Due,
    meta: {
      textClass: 'bg-danger-light text-danger',
    },
  },
  {
    label: 'Past due',
    value: LeaseChargeLedgerEntryStatus.PastDue,
    meta: {
      textClass: 'bg-danger-light text-danger',
    },
  },
  {
    label: 'Paid',
    value: LeaseChargeLedgerEntryStatus.Paid,
    meta: {
      textClass: 'bg-success-light text-success',
    },
  },
  {
    label: 'Failed',
    value: LeaseChargeLedgerEntryStatus.Failed,
    meta: {
      textClass: 'bg-danger-light text-danger',
    },
  },
  {
    label: 'Returned',
    value: LeaseChargeLedgerEntryStatus.Returned,
    meta: {
      textClass: 'bg-grey text-text-03',
    },
  },
  {
    label: 'Processing',
    value: LeaseChargeLedgerEntryStatus.Processing,
    meta: {
      textClass: 'bg-warning-light text-warning',
    },
  },
  {
    label: 'Settled',
    value: LeaseChargeLedgerEntryStatus.Settled,
    meta: {
      textClass: 'bg-success-light text-success',
    },
  },
  {
    label: 'Disputing',
    value: LeaseChargeLedgerEntryStatus.Disputing,
    meta: {
      textClass: 'bg-warning-light text-warning',
    },
  },
]);

export const DEPOSIT_STATUS_DICT = new Dict<LeaseDepositTransactionStatus, { textClass: string }>([
  {
    label: 'Future',
    value: LeaseDepositTransactionStatus.Future,
    meta: {
      textClass: 'bg-success-light text-success',
    },
  },
  {
    label: 'Due',
    value: LeaseDepositTransactionStatus.Due,
    meta: {
      textClass: 'bg-danger-light text-danger',
    },
  },
  {
    label: 'Past due',
    value: LeaseDepositTransactionStatus.PastDue,
    meta: {
      textClass: 'bg-danger-light text-danger',
    },
  },
  {
    label: 'Paid',
    value: LeaseDepositTransactionStatus.Paid,
    meta: {
      textClass: 'bg-success-light text-success',
    },
  },
  {
    label: 'Failed',
    value: LeaseDepositTransactionStatus.Failed,
    meta: {
      textClass: 'bg-danger-light text-danger',
    },
  },
  {
    label: 'Returned',
    value: LeaseDepositTransactionStatus.Returned,
    meta: {
      textClass: 'bg-grey text-text-03',
    },
  },
  {
    label: 'Processing',
    value: LeaseDepositTransactionStatus.Processing,
    meta: {
      textClass: 'bg-warning-light text-warning',
    },
  },
  {
    label: 'Settled',
    value: LeaseDepositTransactionStatus.Settled,
    meta: {
      textClass: 'bg-success-light text-success',
    },
  },
  {
    label: 'Disputing',
    value: LeaseDepositTransactionStatus.Disputing,
    meta: {
      textClass: 'bg-warning-light text-warning',
    },
  },
]);

export const createBillTransactionOptions = (transactions: LeaseChargeLedgerEntryDto[], t: (key: string) => string): Option[] => {
  return transactions.reduce((acc, transaction) => {
    if (transaction.type !== LeaseChargeLedgerEntryType.Charge || transaction.status === LeaseChargeLedgerEntryStatus.Paid) return acc;
    const description = transaction.description || t('Bill');
    const amount = currency(transaction.amount);
    const date = dateTimeFormat(transaction.transactionDate, { timeFormat: false });
    const status = transaction.status
      ? t((transaction.status as string).charAt(0).toUpperCase() + (transaction.status as string).slice(1))
      : '';

    return acc.concat({
      label: `${date} • ${description} • ${amount} • ${status}`,
      value: transaction.id || '',
    });
  }, [] as Option[]);
};
